"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActionRowInternalApi = void 0;
const ApiBase_1 = require("../Implementation/ApiBase");
class ActionRowInternalApi extends ApiBase_1.ApiBase {
    buildRowEditForm(rowNode) {
        return this.buildActionRow('rowEdited', rowNode);
    }
    buildRowCreateForm(clonedRowNode) {
        return this.buildActionRow('rowCreated', clonedRowNode);
    }
    get actionRowFormOptions() {
        return this.getActionRowOptions().actionRowFormOptions;
    }
    get adaptableInstance() {
        return this.getAdaptableApi().internalApi.getAdaptableInstance();
    }
    buildActionRow(type, rowNode) {
        const formFields = this.buildActionRowFields(type, rowNode);
        const formButtons = this.buidActionRowButtons(type, rowNode, formFields);
        const formTitle = this.getFormTitle(type, rowNode);
        const formDescription = this.getFormDescription(type, rowNode);
        const actionRowForm = {
            title: formTitle,
            description: formDescription,
            fields: formFields,
            buttons: formButtons,
        };
        return actionRowForm;
    }
    getFormTitle(type, rowNode) {
        if (this.actionRowFormOptions.formTitle == undefined) {
            return type === 'rowCreated' ? 'Create New Row' : 'Edit Row';
        }
        return typeof this.actionRowFormOptions.formTitle === 'function'
            ? this.actionRowFormOptions.formTitle(this.buildFormParamContext(type, rowNode))
            : this.actionRowFormOptions.formTitle;
    }
    getFormDescription(type, rowNode) {
        return typeof this.actionRowFormOptions.formDescription === 'function'
            ? this.actionRowFormOptions.formDescription(this.buildFormParamContext(type, rowNode))
            : this.actionRowFormOptions.formDescription;
    }
    buildFormParamContext(type, rowNode) {
        return {
            rowNode,
            adaptableApi: this.getAdaptableApi(),
            userName: this.getOptions().userName,
            adaptableId: this.getOptions().adaptableId,
            type: type,
        };
    }
    buildActionRowFields(actionRowType, rowNode) {
        const relevantColumns = this.getAdaptableApi()
            .columnApi.getColumns()
            .filter((column) => {
            // if there is NO rowNode, do NOT display the non-editable fields as they will be empty
            return !!rowNode || this.isColumnEditable(column, rowNode);
        })
            .filter((column) => !this.getAdaptableApi().columnApi.internalApi.isActionRowButtonColumn(column.columnId) &&
            this.showColumnInActionRowForm(column, actionRowType));
        return relevantColumns.map((column) => this.buildFormField(actionRowType, column, rowNode));
    }
    showColumnInActionRowForm(adaptableColumn, actionRowType) {
        const showColumnFn = this.getActionRowOptions().actionRowFormOptions.showColumninActionRowForm;
        if (typeof showColumnFn === 'function') {
            return showColumnFn({
                adaptableApi: this.adaptable.api,
                userName: this.getOptions().userName,
                adaptableId: this.getOptions().adaptableId,
                adaptableColumn,
                actionRowType
            });
        }
        return true;
    }
    buidActionRowButtons(type, rowNode, formFields) {
        // 1. check if there are custom user provided buttons
        if (Array.isArray(this.actionRowFormOptions.formButtons)) {
            return this.actionRowFormOptions.formButtons;
        }
        // 2. else return the standard ones
        const cancelButton = {
            label: 'Cancel',
            buttonStyle: {
                variant: 'raised',
                tone: 'neutral',
            },
        };
        const saveButton = {
            label: 'Save',
            buttonStyle: {
                variant: 'raised',
                tone: 'success',
            },
            onClick: (button, context) => {
                var _a, _b;
                // textOutput should not be saved, their values are set to display values
                // we need to filter out the textOutput fields and add the primaryKey
                const eventInfo = type === 'rowCreated'
                    ? {
                        type: 'rowCreated',
                        formData: context.formData,
                        adaptableApi: context.adaptableApi,
                        userName: this.getOptions().userName,
                        adaptableId: this.getOptions().adaptableId,
                        clonedRowNode: rowNode,
                    }
                    : {
                        type: 'rowEdited',
                        formData: this.prepareEditData(formFields, context),
                        rowNode: rowNode,
                        adaptableApi: context.adaptableApi,
                        userName: this.getOptions().userName,
                        adaptableId: this.getOptions().adaptableId,
                    };
                this.getAdaptableApi().eventApi.emit('ActionRowSubmitted', eventInfo);
                (_b = (_a = this.actionRowFormOptions) === null || _a === void 0 ? void 0 : _a.onFormSubmit) === null || _b === void 0 ? void 0 : _b.call(_a, eventInfo);
            },
        };
        return [cancelButton, saveButton];
    }
    prepareEditData(formFields, context) {
        const dataToSave = Object.assign({}, context.formData);
        for (const formField of formFields) {
            if (formField.fieldType === 'textOutput') {
                delete dataToSave[formField.name];
            }
        }
        const pkValue = this.adaptable.getPrimaryKeyValueFromRowNode(context.rowNode);
        dataToSave[this.getOptions().primaryKey] = pkValue;
        return dataToSave;
    }
    isColumnEditable(column, rowNode) {
        return rowNode
            ? this.adaptableInstance.isCellEditable(rowNode, this.adaptableInstance.getAgGridColumnForColumnId(column.columnId))
            : !column.readOnly;
    }
    buildFormField(type, column, rowNode) {
        const isColumnEditable = this.isColumnEditable(column, rowNode);
        const fieldValueOptions = this.getFieldValueOptions(column, rowNode);
        const fieldType = isColumnEditable
            ? !!(fieldValueOptions === null || fieldValueOptions === void 0 ? void 0 : fieldValueOptions.length)
                ? 'select'
                : this.getFieldTypeFromColumnType(column)
            : 'textOutput';
        const defaultValue = rowNode
            ? isColumnEditable
                ? this.getAdaptableApi().gridApi.getRawValueFromRowNode(rowNode, column.columnId)
                : this.getAdaptableApi().gridApi.getDisplayValueFromRowNode(rowNode, column.columnId)
            : null;
        return {
            label: this.getFormFieldLabel(type, column, rowNode),
            name: column.columnId,
            defaultValue,
            fieldType,
            options: fieldValueOptions,
        };
    }
    getFormFieldLabel(type, column, rowNode) {
        const customFieldLabel = typeof this.actionRowFormOptions.formFieldLabel === 'function'
            ? this.actionRowFormOptions.formFieldLabel(this.buildFormFieldLabelContext(type, column, rowNode))
            : undefined;
        return customFieldLabel !== null && customFieldLabel !== void 0 ? customFieldLabel : column.friendlyName;
    }
    buildFormFieldLabelContext(type, column, rowNode) {
        return {
            rowNode,
            column,
            adaptableApi: this.getAdaptableApi(),
            userName: this.getOptions().userName,
            adaptableId: this.getOptions().adaptableId,
            type: type,
        };
    }
    getFieldTypeFromColumnType(column) {
        switch (column.dataType) {
            case 'Boolean':
                return 'checkbox';
            case 'Date':
                return 'date';
            case 'Number':
                return 'number';
            default:
                // everything else is a plain text input
                return 'text';
        }
    }
    getFieldValueOptions(column, rowNode) {
        const editLookUpItem = this.getAdaptableApi().userInterfaceApi.getEditLookUpItemForColumn(column);
        if (!editLookUpItem) {
            return;
        }
        const gridCell = rowNode
            ? this.getAdaptableApi().gridApi.getGridCellFromRowNode(rowNode, column.columnId)
            : undefined;
        const editLookUpValues = this.getAdaptableApi().gridApi.internalApi.getEditLookUpValuesForColumn(editLookUpItem, column, gridCell);
        return editLookUpValues === null || editLookUpValues === void 0 ? void 0 : editLookUpValues.map((value) => ({
            value,
            label: value,
        }));
    }
}
exports.ActionRowInternalApi = ActionRowInternalApi;
